"use client";

import Link from "next/link";

import type { WalletHelpFaqSectionDTO } from "../../types/cms/profile-page";
import { sanitizeRichText } from "../../utils/sanitize";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../ui/accordion";

const MAX_VISIBLE = 5;

interface WalletHelpFaqSectionProps {
  cms: WalletHelpFaqSectionDTO;
}

export function WalletHelpFaqSection({ cms }: Readonly<WalletHelpFaqSectionProps>) {
  const items = cms.items.slice(0, MAX_VISIBLE);

  const accordionIcon = cms.accordionIconUrl ? (
    <img
      src={cms.accordionIconUrl}
      alt=""
      className="h-4 w-4 shrink-0 transition-transform duration-200 group-data-[state=open]:rotate-180"
      aria-hidden="true"
    />
  ) : undefined;

  return (
    <section className="mt-10" data-testid="help-faq-section">
      <div className="text-left md:text-center">
        <h2 className="text-lg md:text-2xl font-bold text-brand-blue mb-1 leading-snug">
          {cms.heading}
        </h2>
        <p className="text-xs md:text-base text-gray-500 leading-relaxed">{cms.description}</p>
      </div>

      <Accordion
        type="single"
        collapsible
        className="mt-4 space-y-2 md:mx-26"
        data-testid="faq-accordion"
      >
        {items.map((item, index) => (
          <AccordionItem
            key={item.title || String(index)}
            value={item.title || String(index)}
            className="rounded-lg border bg-white px-3 md:px-4"
          >
            <AccordionTrigger
              icon={accordionIcon}
              className="text-xs md:text-sm font-medium text-left text-brand-blue hover:no-underline py-3 md:py-4 leading-snug cursor-pointer"
            >
              {item.title}
            </AccordionTrigger>
            <AccordionContent className="text-xs md:text-sm text-gray-600 pb-2 md:pb-3 leading-relaxed">
              <div dangerouslySetInnerHTML={{ __html: sanitizeRichText(item.description) }} />
              {item.link && item.linkText && (
                <Link
                  href={item.link}
                  className="mt-2 inline-block text-brand-cyan underline hover:no-underline"
                >
                  {item.linkText}
                </Link>
              )}
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>

      {(cms.seeAllMobileLabel || cms.seeAllDesktopLabel) && (
        <div className="mt-6 text-center">
          <Link
            href={cms.seeAllHref || "#"}
            className="text-xs md:text-sm font-medium text-brand-cyan underline"
            data-testid="help-faq-see-all"
          >
            <span className="md:hidden">{cms.seeAllMobileLabel}</span>
            <span className="hidden md:inline">{cms.seeAllDesktopLabel}</span>
          </Link>
        </div>
      )}
    </section>
  );
}
